import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Sparkles } from "lucide-react";

/**
 * XPBadge — Iter70 / Phase 3. A pill in the header showing the user's
 * current level + a thin progress ring for XP into the next level.
 *
 * Sits next to the streak / wallet pills. Polled once on mount, and
 * refreshes whenever any other component fires `oak:xp-updated` with
 * a fresh XP snapshot in `event.detail`.
 *
 * When the level number increases between two snapshots, we also fire
 * `oak:level-up` with the same payload — `LevelUpModal` listens and
 * pops the celebration. Decoupled this way so we don't have to wire
 * the modal into every XP-awarding endpoint individually.
 */

// Module-level cache so tab-switches don't flash an empty badge.
let _cachedXp = null;
let _lastLevel = null;

export default function XPBadge() {
  const [snap, setSnap] = useState(_cachedXp);
  // Phase 5 motion — when XP increases between snapshots we float a
  // small "+N" above the badge. Auto-dismisses after the animation.
  const [gain, setGain] = useState(null); // { amount, key }

  const apply = (data) => {
    if (!data || typeof data.level !== "number") return;
    const prevXp = _cachedXp?.xp ?? null;
    _cachedXp = data;
    setSnap(data);
    if (prevXp != null && data.xp > prevXp) {
      const delta = data.xp - prevXp;
      // Cap absurd jumps (e.g. first-time load skew) so the floater
      // doesn't display +9000 on a returning user.
      if (delta > 0 && delta <= 200) {
        setGain({ amount: delta, key: Date.now() });
      }
    }
    // Level-up detection — fire global event for LevelUpModal.
    if (_lastLevel != null && data.level > _lastLevel) {
      window.dispatchEvent(new CustomEvent("oak:level-up", {
        detail: { ...data, previous_level: _lastLevel },
      }));
    }
    _lastLevel = data.level;
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/xp/me")
      .then(({ data }) => { if (!cancelled) apply(data); })
      .catch(() => {});
    const onUpdate = (e) => {
      // The `oak:xp-updated` event may not carry a snapshot (the global
      // axios interceptor fires a payload-less version). In that case
      // re-fetch the badge so we still see the gain animation.
      if (e?.detail && typeof e.detail.level === "number") {
        apply(e.detail);
      } else {
        api.get("/xp/me").then(({ data }) => { if (!cancelled) apply(data); }).catch(() => {});
      }
    };
    window.addEventListener("oak:xp-updated", onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("oak:xp-updated", onUpdate);
    };
  }, []);

  // Iter79 — explainer popover. Many users ask "what's this Sprout
  // counter?". A click opens a small popover that demystifies the
  // ring + level title + next-level math without bloating the header.
  // Declared up here (before any early return) — Rules of Hooks.
  const [showExplainer, setShowExplainer] = useState(false);

  if (!snap || snap.level == null) return null;
  const pct = Math.min(1, Math.max(0, snap.progress_pct || 0));
  const ring = 28;  // outer SVG size in px
  const r = 12;     // ring radius
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);

  return (
    <div
      className="hidden sm:inline-block relative"
      data-testid="xp-badge-wrap"
    >
    <button
      type="button"
      onClick={() => setShowExplainer((v) => !v)}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-white/12 bg-white/[0.05] text-white/85 transition hover:border-white/25 relative"
      title={`${snap.title} · Level ${snap.level} · ${snap.xp_into_level} / ${snap.xp_to_next_level} XP to next · click for details`}
      data-testid="xp-badge"
      data-level={snap.level}
      aria-label={`${snap.title} Level ${snap.level}, ${snap.xp_into_level} of ${snap.xp_to_next_level} XP to next level. Click to learn how XP works.`}
    >
      {/* Floating XP gain — appears momentarily on increase, then fades up. */}
      <AnimatePresence>
        {gain && (
          <motion.span
            key={gain.key}
            initial={{ opacity: 0, y: 4, scale: 0.9 }}
            animate={{ opacity: 1, y: -22, scale: 1 }}
            exit={{ opacity: 0, y: -36 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            onAnimationComplete={() => setGain(null)}
            className="absolute left-1/2 -translate-x-1/2 -top-1 text-[10.5px] font-display font-bold tracking-tight text-[#9bffcc] pointer-events-none drop-shadow-[0_2px_6px_rgba(0,255,157,0.5)]"
            aria-hidden="true"
            data-testid="xp-gain-float"
          >
            +{gain.amount}&nbsp;XP
          </motion.span>
        )}
      </AnimatePresence>
      <div className="relative" style={{ width: ring, height: ring }}>
        <svg width={ring} height={ring} viewBox={`0 0 ${ring} ${ring}`}>
          <circle
            cx={ring / 2} cy={ring / 2} r={r}
            stroke="rgba(255,255,255,0.08)" strokeWidth="2" fill="none"
          />
          <circle
            cx={ring / 2} cy={ring / 2} r={r}
            stroke="url(#xp-grad)" strokeWidth="2.5" fill="none"
            strokeDasharray={c} strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${ring / 2} ${ring / 2})`}
            style={{ transition: "stroke-dashoffset 600ms cubic-bezier(0.4,0,0.2,1)" }}
          />
          <defs>
            <linearGradient id="xp-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00FF9D" />
              <stop offset="100%" stopColor="#0044FF" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 grid place-items-center text-[10px] font-display font-bold tabular-nums leading-none">
          {snap.level}
        </div>
      </div>
      <span className="text-[11px] font-display font-semibold tracking-tight max-w-[80px] truncate hidden md:inline">
        {snap.title}
      </span>
    </button>

    {/* Iter79 — XP/level explainer popover. Demystifies the small
        ring counter at the top of the header (people commonly ask
        "what's this Sprout / Seedling?"). Click toggles; click
        outside (via the overlay) closes. */}
    {showExplainer && (
      <>
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowExplainer(false)}
          aria-hidden="true"
        />
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-40 w-72 rounded-xl border border-white/12 bg-[#0B0E14]/95 backdrop-blur-md shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] p-4"
          role="dialog"
          aria-label="What is the XP counter?"
          data-testid="xp-explainer-popover"
        >
          <div className="flex items-center gap-2 mb-2">
            <div
              className="rounded-lg p-1.5 border"
              style={{ background: "#00FF9D1A", borderColor: "#00FF9D55" }}
            >
              <Sparkles size={13} style={{ color: "#00FF9D" }} />
            </div>
            <div className="font-display text-[13px] text-white tracking-tight">
              You're a <span className="text-[#9bffcc]">{snap.title}</span>
            </div>
          </div>
          <p className="text-[12px] text-white/70 leading-relaxed">
            That ring shows your level <span className="text-white">{snap.level}</span> — you earn <span className="text-white">XP</span> for everything: streaks, quizzes, flashcards, mini-games, doubts answered. Fill the ring to level up.
          </p>
          <div className="mt-3 grid grid-cols-3 gap-1.5 text-[10px] font-mono-oak">
            <div className="bg-white/[0.04] border border-white/8 rounded-md px-2 py-1.5 text-center">
              <div className="text-white/45">Now</div>
              <div className="text-white text-[12px] font-display tabular-nums">{snap.xp}</div>
            </div>
            <div className="bg-white/[0.04] border border-white/8 rounded-md px-2 py-1.5 text-center">
              <div className="text-white/45">Into level</div>
              <div className="text-white text-[12px] font-display tabular-nums">{snap.xp_into_level}</div>
            </div>
            <div className="bg-white/[0.04] border border-white/8 rounded-md px-2 py-1.5 text-center">
              <div className="text-white/45">To next</div>
              <div className="text-[#9bffcc] text-[12px] font-display tabular-nums">{snap.xp_to_next_level - snap.xp_into_level}</div>
            </div>
          </div>
          <div className="text-[10.5px] text-white/45 mt-3 leading-relaxed">
            Levels: Sprout → Seedling → Sapling → Apprentice → Scholar → and beyond. Cosmetic only — no paywalls, no resets.
          </div>
        </div>
      </>
    )}
    </div>
  );
}
